import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  MapPin, 
  Building2, 
  Phone, 
  Users,
  AlertTriangle,
  Check,
  X
} from 'lucide-react';
import { SHOW_INSTITUTO_BW_BRANDING } from '../lib/branding';

const ESTADOS_BR = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 
  'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 
  'SP', 'SE', 'TO'
];

export default function BaseManagement() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [bases, setBases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedBase, setSelectedBase] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state for new base
  const [name, setName] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [capacity, setCapacity] = useState('50');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');

  // Form state for edit
  const [editName, setEditName] = useState('');
  const [editCity, setEditCity] = useState('');
  const [editState, setEditState] = useState('');
  const [editLatitude, setEditLatitude] = useState('');
  const [editLongitude, setEditLongitude] = useState('');
  const [editCapacity, setEditCapacity] = useState('');
  const [editAddress, setEditAddress] = useState('');
  const [editPhone, setEditPhone] = useState('');

  useEffect(() => {
    fetchBases();
  }, []);

  const fetchBases = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/bases`);
      setBases(response.data);
    } catch (error) {
      toast.error('Erro ao carregar bases');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setName('');
    setCity('');
    setState('');
    setLatitude('');
    setLongitude('');
    setCapacity('50');
    setAddress('');
    setPhone('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/bases`, { 
        name, 
        city, 
        state,
        latitude: latitude ? parseFloat(latitude) : null,
        longitude: longitude ? parseFloat(longitude) : null,
        capacity: parseInt(capacity) || 50,
        address: address || null,
        phone: phone || null
      });
      toast.success('Base criada com sucesso!');
      setDialogOpen(false);
      resetForm();
      fetchBases();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar base');
    } finally {
      setSaving(false);
    }
  };

  const openEditDialog = (base) => {
    setSelectedBase(base);
    setEditName(base.name);
    setEditCity(base.city);
    setEditState(base.state);
    setEditLatitude(base.latitude?.toString() || '');
    setEditLongitude(base.longitude?.toString() || '');
    setEditCapacity(base.capacity?.toString() || '50');
    setEditAddress(base.address || '');
    setEditPhone(base.phone || '');
    setEditDialogOpen(true);
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!selectedBase) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/bases/${selectedBase.id}`, {
        name: editName,
        city: editCity,
        state: editState,
        latitude: editLatitude ? parseFloat(editLatitude) : null,
        longitude: editLongitude ? parseFloat(editLongitude) : null,
        capacity: parseInt(editCapacity) || 50,
        address: editAddress || null,
        phone: editPhone || null
      });
      toast.success('Base atualizada com sucesso!');
      setEditDialogOpen(false);
      setSelectedBase(null);
      fetchBases();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar base');
    } finally {
      setSaving(false);
    }
  };

  const openDeleteDialog = (base) => {
    setSelectedBase(base);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedBase) return;
    
    setSaving(true);
    try {
      await axios.delete(`${API}/bases/${selectedBase.id}`);
      toast.success('Base excluída com sucesso!');
      setDeleteDialogOpen(false);
      setSelectedBase(null);
      fetchBases();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir base');
    } finally {
      setSaving(false);
    }
  };

  const hasMapData = (base) => {
    return base.latitude && base.longitude;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-green-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-emerald-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {SHOW_INSTITUTO_BW_BRANDING && (
                <img
                  src="/bw-symbol-final.png"
                  alt="Instituto BW"
                  className="h-12 w-12 object-contain"
                />
              )}
              <div className={SHOW_INSTITUTO_BW_BRANDING ? 'border-l border-emerald-200 pl-3' : ''}>
                <h1 className="text-xl font-bold text-emerald-800" style={{ fontFamily: 'Playfair Display, serif' }}>Gerenciar Bases</h1>
                {SHOW_INSTITUTO_BW_BRANDING && (
                  <p className="text-xs text-emerald-500">Instituto BW - Sistema CRAS</p>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-emerald-800">{user?.name}</p>
                <p className="text-xs text-emerald-600 capitalize">{user?.role}</p>
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  onClick={() => navigate('/')}
                  variant="outline"
                  className="border-emerald-600 text-emerald-600 hover:bg-emerald-50"
                  data-testid="back-to-dashboard-button"
                >
                  Voltar ao Dashboard
                </Button>
                <Button onClick={logout} variant="ghost" className="text-red-600 hover:bg-red-50" data-testid="logout-button">
                  Sair
                </Button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-emerald-800" style={{ fontFamily: 'Playfair Display, serif' }}>
            Bases Cadastradas ({bases.length})
          </h2>
          
          {/* Dialog Nova Base */}
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="new-base-button">
                <Plus className="w-4 h-4 mr-2" />
                Nova Base
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-emerald-800">
                  <Building2 className="w-5 h-5" />
                  Criar Nova Base
                </DialogTitle>
                <DialogDescription>
                  Preencha os dados da nova base. Campos com * são obrigatórios.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <Label htmlFor="name">Nome da Base *</Label>
                    <Input
                      id="name"
                      data-testid="base-name-input"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      placeholder="Ex: CRAS São Paulo"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="city">Cidade *</Label>
                      <Input
                        id="city"
                        data-testid="base-city-input"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        required
                        placeholder="Ex: São Paulo"
                      />
                    </div>
                    <div>
                      <Label htmlFor="state">Estado *</Label>
                      <Select value={state} onValueChange={setState} required>
                        <SelectTrigger data-testid="base-state-input">
                          <SelectValue placeholder="UF" />
                        </SelectTrigger>
                        <SelectContent>
                          {ESTADOS_BR.map(uf => (
                            <SelectItem key={uf} value={uf}>{uf}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="address">Endereço</Label>
                    <Input
                      id="address"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      placeholder="Rua, número, bairro"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="phone">Telefone</Label>
                      <Input
                        id="phone"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="(11) 99999-9999"
                      />
                    </div>
                    <div>
                      <Label htmlFor="capacity">Capacidade</Label>
                      <Input
                        id="capacity"
                        type="number"
                        value={capacity}
                        onChange={(e) => setCapacity(e.target.value)}
                        placeholder="50"
                        min="1"
                      />
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <Label className="flex items-center gap-2 mb-3">
                      <MapPin className="w-4 h-4 text-emerald-600" />
                      Localização no Mapa (para visualização no Mapa de Bases)
                    </Label>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="latitude">Latitude</Label>
                        <Input
                          id="latitude"
                          type="number"
                          step="any"
                          value={latitude}
                          onChange={(e) => setLatitude(e.target.value)}
                          placeholder="-23.5505"
                        />
                      </div>
                      <div>
                        <Label htmlFor="longitude">Longitude</Label>
                        <Input
                          id="longitude"
                          type="number"
                          step="any"
                          value={longitude}
                          onChange={(e) => setLongitude(e.target.value)}
                          placeholder="-46.6333"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Dica: Use o Google Maps para obter as coordenadas. Clique com botão direito no local e copie as coordenadas.
                    </p>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-emerald-600 hover:bg-emerald-700" 
                  data-testid="submit-base-button"
                  disabled={saving || !name || !city || !state}
                >
                  {saving ? 'Salvando...' : 'Criar Base'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Card className="shadow-sm border-emerald-100">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
              </div>
            ) : bases.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Building2 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>Nenhuma base cadastrada</p>
                <p className="text-sm">Clique em "Nova Base" para começar</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome</TableHead>
                      <TableHead>Localização</TableHead>
                      <TableHead>Capacidade</TableHead>
                      <TableHead>Mapa</TableHead>
                      <TableHead>Contato</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bases.map((base) => (
                      <TableRow key={base.id} data-testid={`base-row-${base.id}`}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{base.name}</p>
                            {base.address && (
                              <p className="text-xs text-gray-500">{base.address}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <MapPin className="w-4 h-4 text-gray-400" />
                            {base.city} - {base.state}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Users className="w-4 h-4 text-gray-400" />
                            {base.capacity || 50} animais
                          </div>
                        </TableCell>
                        <TableCell>
                          {hasMapData(base) ? (
                            <Badge className="bg-green-100 text-green-700 flex items-center gap-1 w-fit">
                              <Check className="w-3 h-3" />
                              Configurado
                            </Badge>
                          ) : (
                            <Badge className="bg-yellow-100 text-yellow-700 flex items-center gap-1 w-fit">
                              <AlertTriangle className="w-3 h-3" />
                              Sem coordenadas
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {base.phone ? (
                            <div className="flex items-center gap-1 text-sm">
                              <Phone className="w-3 h-3 text-gray-400" />
                              {base.phone}
                            </div>
                          ) : (
                            <span className="text-gray-400 text-sm">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openEditDialog(base)}
                              className="border-emerald-300 text-emerald-600 hover:bg-emerald-50"
                              data-testid={`edit-base-${base.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openDeleteDialog(base)}
                              className="border-red-300 text-red-600 hover:bg-red-50"
                              data-testid={`delete-base-${base.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="mt-6 border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-blue-800">Sobre o Mapa de Bases</h3>
                <p className="text-sm text-blue-700 mt-1">
                  Para que uma base apareça corretamente no <strong>Mapa de Bases</strong>, é necessário cadastrar as coordenadas 
                  (latitude e longitude). Bases sem coordenadas ficarão marcadas como "Sem coordenadas" e não aparecerão no mapa interativo.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-800">
              <Pencil className="w-5 h-5" />
              Editar Base
            </DialogTitle>
            <DialogDescription>
              Atualize os dados da base "{selectedBase?.name}"
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEdit} className="space-y-4 mt-4">
            <div className="grid grid-cols-1 gap-4">
              <div>
                <Label htmlFor="editName">Nome da Base *</Label>
                <Input
                  id="editName"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  required
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="editCity">Cidade *</Label>
                  <Input
                    id="editCity"
                    value={editCity}
                    onChange={(e) => setEditCity(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="editState">Estado *</Label>
                  <Select value={editState} onValueChange={setEditState} required>
                    <SelectTrigger>
                      <SelectValue placeholder="UF" />
                    </SelectTrigger>
                    <SelectContent>
                      {ESTADOS_BR.map(uf => (
                        <SelectItem key={uf} value={uf}>{uf}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="editAddress">Endereço</Label>
                <Input
                  id="editAddress"
                  value={editAddress}
                  onChange={(e) => setEditAddress(e.target.value)}
                  placeholder="Rua, número, bairro"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="editPhone">Telefone</Label>
                  <Input
                    id="editPhone"
                    value={editPhone}
                    onChange={(e) => setEditPhone(e.target.value)}
                    placeholder="(11) 99999-9999"
                  />
                </div>
                <div>
                  <Label htmlFor="editCapacity">Capacidade</Label>
                  <Input
                    id="editCapacity"
                    type="number"
                    value={editCapacity}
                    onChange={(e) => setEditCapacity(e.target.value)}
                    min="1"
                  />
                </div>
              </div>

              <div className="border-t pt-4">
                <Label className="flex items-center gap-2 mb-3">
                  <MapPin className="w-4 h-4 text-emerald-600" />
                  Localização no Mapa
                </Label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="editLatitude">Latitude</Label>
                    <Input
                      id="editLatitude"
                      type="number"
                      step="any"
                      value={editLatitude}
                      onChange={(e) => setEditLatitude(e.target.value)}
                      placeholder="-23.5505"
                    />
                  </div>
                  <div>
                    <Label htmlFor="editLongitude">Longitude</Label>
                    <Input
                      id="editLongitude"
                      type="number"
                      step="any"
                      value={editLongitude}
                      onChange={(e) => setEditLongitude(e.target.value)}
                      placeholder="-46.6333"
                    />
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setEditDialogOpen(false)}
              >
                Cancelar
              </Button>
              <Button 
                type="submit" 
                className="bg-emerald-600 hover:bg-emerald-700"
                disabled={saving || !editName || !editCity || !editState}
              >
                {saving ? 'Salvando...' : 'Salvar Alterações'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Excluir Base
            </DialogTitle>
            <DialogDescription>
              Tem certeza que deseja excluir a base <strong>"{selectedBase?.name}"</strong>?
              <br /><br />
              <span className="text-red-600">
                Esta ação não pode ser desfeita. A base só pode ser excluída se não houver animais vinculados.
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button 
              variant="outline" 
              onClick={() => setDeleteDialogOpen(false)}
              disabled={saving}
            >
              Cancelar
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDelete}
              disabled={saving}
            >
              {saving ? 'Excluindo...' : 'Excluir Base'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
